import { useState, useEffect, useRef } from 'react'
import { CalendarClock, FileSignature, X, Check, Loader2, Printer, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import comissaoPrazoService from '../../services/comissaoPrazoService'

const PRODUTO = 'prazo_comissao'

const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

const fmtData = iso => {
  if (!iso) return ''
  const [a, m, d] = String(iso).slice(0, 10).split('-')
  return `${d}/${m}/${a}`
}

// data do leilão (YYYY-MM-DD) + dias, sem depender de fuso horário
const somarDias = (iso, dias) => {
  if (!iso) return ''
  const [a, m, d] = String(iso).slice(0, 10).split('-').map(Number)
  const dt = new Date(Date.UTC(a, m - 1, d + (parseInt(dias, 10) || 0)))
  return dt.toISOString().slice(0, 10)
}

const rotuloPrazo = dias => (dias <= 0 ? 'à vista' : `${dias} dia${dias === 1 ? '' : 's'}`)

/**
 * Prazo da comissão do comprador (fluxo "comissão na primeira parcela").
 * Produto premium `prazo_comissao`. Renderiza botão(ões) na barra de ações; o modal mostra o
 * detalhamento (comissão de compra + venda + despesas), pergunta o prazo em dias (0 = à vista)
 * e permite imprimir a nota promissória da comissão logo após salvar ou depois.
 *
 * comprador: { id, nome, comissao_prazo: { prazo_dias, data_vencimento } | null }
 */
export default function PrazoComissao({ orgId, leilaoId, fluxo, comprador, onChanged }) {
  const { organizadorAtivo } = useOrganizador()
  const [aberto, setAberto] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [gerandoPdf, setGerandoPdf] = useState(false)
  const iframeRef = useRef(null)

  const ativo = (organizadorAtivo?.produtos_premium || []).includes(PRODUTO)
  if (!ativo || fluxo !== 'primeira_parcela' || !comprador?.id) return null

  const prazoAtual = comprador.comissao_prazo

  const imprimirNota = async () => {
    setGerandoPdf(true)
    try {
      const blob = await comissaoPrazoService.notaPromissoriaPdf(orgId, leilaoId, comprador.id)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      setPdfUrl(URL.createObjectURL(new Blob([blob], { type: 'application/pdf' })))
    } catch (err) {
      toast.error('Erro ao gerar a nota promissória')
    } finally {
      setGerandoPdf(false)
    }
  }

  const fecharPdf = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl)
    setPdfUrl(null)
  }

  const imprimirIframe = () => {
    iframeRef.current?.contentWindow?.focus()
    iframeRef.current?.contentWindow?.print()
  }

  return (
    <>
      <button
        onClick={() => setAberto(true)}
        className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors"
        title="Definir o prazo para o comprador pagar a comissão e as despesas"
      >
        <CalendarClock className="w-3.5 h-3.5" />
        <span>
          {prazoAtual ? `Comissão vence ${fmtData(prazoAtual.data_vencimento)}` : 'Prazo da comissão'}
        </span>
      </button>

      {prazoAtual && (
        <button
          onClick={imprimirNota}
          disabled={gerandoPdf}
          className="flex items-center gap-1.5 px-3 py-2 bg-slate-700 hover:bg-slate-800 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-60"
        >
          {gerandoPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSignature className="w-3.5 h-3.5" />}
          <span className="hidden sm:inline">{gerandoPdf ? 'Gerando...' : 'Nota promissória da comissão'}</span>
          <span className="sm:hidden">Promissória</span>
        </button>
      )}

      {aberto && (
        <PrazoModal
          orgId={orgId}
          leilaoId={leilaoId}
          comprador={comprador}
          onClose={() => setAberto(false)}
          onSalvo={() => onChanged && onChanged()}
          onImprimir={async () => { setAberto(false); await imprimirNota() }}
        />
      )}

      {pdfUrl && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-3">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[92vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800">Nota promissória da comissão — {comprador.nome}</h3>
              <div className="flex items-center gap-2">
                <button onClick={imprimirIframe} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg">
                  <Printer className="w-3.5 h-3.5" /> Imprimir
                </button>
                <button onClick={fecharPdf} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <iframe ref={iframeRef} src={pdfUrl} title="Nota promissória" className="flex-1 w-full" />
          </div>
        </div>
      )}
    </>
  )
}

function PrazoModal({ orgId, leilaoId, comprador, onClose, onSalvo, onImprimir }) {
  const [info, setInfo] = useState(null)
  const [erro, setErro] = useState(null)
  const [dias, setDias] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [salvo, setSalvo] = useState(false)

  useEffect(() => {
    let cancel = false
    comissaoPrazoService.obter(orgId, leilaoId, comprador.id)
      .then(r => {
        if (cancel) return
        setInfo(r)
        setDias(r.prazo ? String(r.prazo.prazo_dias) : '')
      })
      .catch(e => !cancel && setErro(e.response?.data?.detail || 'Erro ao carregar os valores da comissão'))
    return () => { cancel = true }
  }, [orgId, leilaoId, comprador.id])

  const diasNum = dias === '' ? null : Math.max(0, parseInt(dias, 10) || 0)
  const dataVenc = info && diasNum != null ? somarDias(info.leilao.data_leilao, diasNum) : ''
  const jaPaga = info?.conta?.status === 'pago'

  const salvar = async (imprimir = false) => {
    if (diasNum == null) return
    setSalvando(true)
    try {
      const r = await comissaoPrazoService.salvar(orgId, leilaoId, comprador.id, diasNum)
      setInfo(r)
      setSalvo(true)
      toast.success('Prazo da comissão salvo')
      onSalvo()
      if (imprimir) onImprimir()
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar o prazo')
    } finally {
      setSalvando(false)
    }
  }

  const remover = async () => {
    if (!window.confirm('Remover o prazo? O vencimento volta a ser a data do leilão.')) return
    setSalvando(true)
    try {
      const r = await comissaoPrazoService.remover(orgId, leilaoId, comprador.id)
      setInfo(r)
      setDias('')
      setSalvo(false)
      toast.success('Prazo removido')
      onSalvo()
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao remover o prazo')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center">
              <CalendarClock size={16} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Prazo da comissão</h2>
              <p className="text-xs text-gray-500">{comprador.nome}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {erro && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{erro}</div>}
          {!info && !erro && (
            <div className="flex items-center justify-center py-10 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          )}

          {info && (
            <>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Lote</th>
                      <th className="px-3 py-2 text-right font-medium">Valor do lote</th>
                      <th className="px-3 py-2 text-right font-medium">Comissão compra</th>
                      <th className="px-3 py-2 text-right font-medium">Comissão venda</th>
                      <th className="px-3 py-2 text-right font-medium">Despesas</th>
                      <th className="px-3 py-2 text-right font-medium">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {info.lotes.map(l => (
                      <tr key={l.lote_id}>
                        <td className="px-3 py-2 font-medium text-gray-800">{l.numero_lote}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{brl(l.valor_venda_total)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{brl(l.valor_comissao_compra)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{brl(l.valor_comissao_venda)}</td>
                        <td className="px-3 py-2 text-right tabular-nums" title={(l.despesas_itens || []).map(d => `${d.titulo}: ${brl(d.valor)}`).join('\n')}>
                          {brl(l.despesas_lote)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{brl(l.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 font-semibold">
                    <tr>
                      <td className="px-3 py-2" colSpan={2}>Total</td>
                      <td className="px-3 py-2 text-right tabular-nums">{brl(info.totais.comissao_compra)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{brl(info.totais.comissao_venda)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{brl(info.totais.despesas)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{brl(info.totais.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="flex items-center justify-between rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                <span className="text-sm text-amber-800 font-medium">Total a pagar (comissões + despesas)</span>
                <span className="text-lg font-bold text-amber-900 tabular-nums">{brl(info.totais.total)}</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500">Prazo para pagamento (dias)</label>
                  <input
                    type="number" min="0" max="3650" inputMode="numeric"
                    value={dias}
                    onChange={e => { setDias(e.target.value); setSalvo(false) }}
                    placeholder="0 = à vista, na data do leilão"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div className="text-sm text-gray-600 pb-2">
                  {diasNum != null && dataVenc ? (
                    <>Vencimento: <b className="text-gray-900">{fmtData(dataVenc)}</b> <span className="text-gray-400">({rotuloPrazo(diasNum)} após o leilão)</span></>
                  ) : (
                    <span className="text-gray-400">Informe os dias para ver a data de vencimento</span>
                  )}
                </div>
              </div>

              {jaPaga && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                  Esta comissão já está baixada na gestão. Alterar o prazo muda só o vencimento registrado.
                </div>
              )}
              {!info.conta && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-500">
                  A conta a receber ainda não foi gerada na gestão. O vencimento será aplicado quando ela for sincronizada.
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-4 border-t border-gray-100 bg-gray-50">
          <div>
            {info?.prazo && (
              <button onClick={remover} disabled={salvando} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50">
                <Trash2 className="w-3.5 h-3.5" /> Remover prazo
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
              {salvo ? 'Fechar' : 'Cancelar'}
            </button>
            {info?.prazo && (
              <button onClick={onImprimir} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-slate-700 hover:bg-slate-800 text-white rounded-lg">
                <FileSignature className="w-4 h-4" /> Nota promissória
              </button>
            )}
            <button
              onClick={() => salvar(false)}
              disabled={salvando || diasNum == null || !info}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-amber-500 hover:bg-amber-600 text-white rounded-lg disabled:opacity-50"
            >
              {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Salvar prazo
            </button>
            <button
              onClick={() => salvar(true)}
              disabled={salvando || diasNum == null || !info}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-slate-700 hover:bg-slate-800 text-white rounded-lg disabled:opacity-50"
            >
              <FileSignature className="w-4 h-4" /> Salvar e imprimir nota
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
